import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Label from 'core/components/label/label';
import { STALE_REASON_TEXT, formatDateTime } from '../aor-runs-table/helpers';
import useStyles from './aor-run-detail.styles';

/**
 * The run's lifecycle, in order, as one machine now drives it: previewed → approved → applying →
 * result. Only a node with a real timestamp behind it is marked reached, so the page never implies a
 * step it cannot date.
 *
 * There is deliberately no separate "created" node: WriteAorRun writes the row at the END of the
 * preview, so `createdAt` IS the moment the run became `previewed`.
 */
const RESULT_LABELS = {
  applied: 'Applied',
  partially_applied: 'Partially applied',
  failed: 'Failed',
  stale: 'Stale — never applied'
};

const TERMINAL_STATUSES = Object.keys(RESULT_LABELS);

const RESULT_DOT_CLASS = {
  applied: 'timelineDotSuccess',
  partially_applied: 'timelineDotWarning',
  failed: 'timelineDotError',
  stale: 'timelineDotError'
};

const RunTimeline = ({ run, applyStartedAt }) => {
  const classes = useStyles();
  const isTerminal = TERMINAL_STATUSES.includes(run.status);

  const nodes = [
    { key: 'previewed', label: 'Previewed', at: run.createdAt, reached: true },
    { key: 'approved', label: 'Approved', at: run.approvedAt, actor: run.approvedBy, reached: !!run.approvedAt },
    { key: 'applying', label: 'Applying', at: applyStartedAt, reached: !!applyStartedAt },
    {
      key: 'result',
      label: isTerminal ? RESULT_LABELS[run.status] : 'Result',
      at: isTerminal ? run.updatedAt : null,
      reached: isTerminal,
      dotClass: isTerminal ? RESULT_DOT_CLASS[run.status] : null
    }
  ];

  return (
    <div className={classes.timeline}>
      <div className={classes.timelineRail} data-testid="timeline-rail">
        {nodes.map((node, index) => (
          <div
            key={node.key}
            className={classNames(classes.timelineNode, { [classes.timelinePending]: !node.reached })}
          >
            <div className={classes.timelineMarker}>
              <span
                className={classNames(classes.timelineDot, {
                  [classes.timelineDotReached]: node.reached && !node.dotClass,
                  [classes[node.dotClass]]: !!node.dotClass
                })}
              />
              {index < nodes.length - 1 && (
                <span
                  className={classNames(classes.timelineLine, { [classes.timelineLineReached]: node.reached })}
                  data-testid="timeline-connector"
                />
              )}
            </div>
            <div className={classes.timelineContent}>
              <Label type="infoValue">{node.label}</Label>
              <Label type="infoLabel">{node.at ? formatDateTime(node.at) : 'Not reached'}</Label>
              {node.actor && <Label type="infoLabel">{`by ${node.actor}`}</Label>}
            </div>
          </div>
        ))}
      </div>
      {/* Below the rail, not inside a column: it is about the run, not about one step, and a
          full-width line is the only place it does not squeeze a node to a quarter of the width.

          Derived staleness is a DIFFERENT fact from the persisted `stale` status: it appears on a run
          still waiting for someone to act, whose mappings drifted underneath it. */}
      {run.isStale && !isTerminal && (
        <Label type="infoValue" className={classes.staleWarning}>
          {`${STALE_REASON_TEXT[run.staleReason] ?? 'Stale'} — re-run the preview to start a fresh cycle.`}
        </Label>
      )}
    </div>
  );
};

RunTimeline.propTypes = {
  run: PropTypes.shape({
    status: PropTypes.string,
    createdAt: PropTypes.string,
    approvedAt: PropTypes.string,
    approvedBy: PropTypes.string,
    updatedAt: PropTypes.string,
    isStale: PropTypes.bool,
    staleReason: PropTypes.string
  }).isRequired,
  applyStartedAt: PropTypes.string
};

RunTimeline.defaultProps = {
  applyStartedAt: null
};

export default RunTimeline;
